import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import RoleRedirect from './components/RoleRedirect';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import RaceEventsPage from './pages/RaceEventsPage';
import CrewPage from './pages/CrewPage';
import DocumentsPage from './pages/DocumentsPage';
import BoatInfoPage from './pages/BoatInfoPage';
import EventSelectionPage from './pages/EventSelectionPage';
import CrewListBuilderPage from './pages/CrewListBuilderPage';
import ComplianceDashboard from './pages/ComplianceDashboard';
import AdminLayout from './pages/admin/AdminLayout';
import SpecialRegulationsManager from './pages/admin/SpecialRegulationsManager';
import RacingRulesManager from './pages/admin/RacingRulesManager';
import IRCRulesManager from './pages/admin/IRCRulesManager';
import ORCiRulesManager from './pages/admin/ORCiRulesManager';
import WhatsAppSettings from './pages/admin/WhatsAppSettings';
import EventLayout from './pages/event/EventLayout';
import RaceEventManager from './pages/admin/RaceEventManager';
import './App.css';

/**
 * Root application component with React Router configuration.
 *
 * Role-based routing:
 *  - admin  → /admin (Special Regulations, Global Documents, Categories)
 *  - event  → /events (Race Events, Event Documents)
 *  - user   → / (Race Events, Crew, Documents)
 *
 * Routes:
 *  /login                          — Login page (public)
 *  /                               — Role-based redirect (protected)
 *  /crew                           — Crew management (user)
 *  /documents                      — Document management (user)
 *  /race-events                    — Event selection for crew list building (user)
 *  /events/:raceId/crew            — Crew list builder for event (user)
 *  /events/:raceId/compliance      — Compliance dashboard for event (user)
 *  /admin/*                        — Admin portal (admin only)
 *  /events/*                       — Event management portal (event only)
 */
const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            {/* Role-based home redirect */}
            <Route path="/" element={<RoleRedirect />} />

            {/* User routes */}
            <Route path="/dashboard" element={<RaceEventsPage />} />
            <Route path="/crew" element={<CrewPage />} />
            <Route path="/boat-info" element={<BoatInfoPage />} />
            <Route path="/documents" element={<DocumentsPage />} />
            
            {/* Event crew management routes */}
            <Route path="/race-events" element={<EventSelectionPage />} />
            
            {/* Event-specific routes */}
            <Route path="/events/:raceId/crew" element={<CrewListBuilderPage />} />
            <Route path="/events/:raceId/compliance" element={<ComplianceDashboard />} />
            <Route path="/events/:raceId/dashboard" element={<ComplianceDashboard />} />

            {/* Admin routes */}
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Navigate to="special-regulations" replace />} />
              <Route path="special-regulations" element={<SpecialRegulationsManager />} />
              <Route path="racing-rules" element={<RacingRulesManager />} />
              <Route path="irc-rules" element={<IRCRulesManager />} />
              <Route path="orci-rules" element={<ORCiRulesManager />} />
              <Route path="whatsapp" element={<WhatsAppSettings />} />
            </Route>

            {/* Event manager routes */}
            <Route path="/events" element={<EventLayout />}>
              <Route index element={<Navigate to="race-events" replace />} />
              <Route path="race-events" element={<RaceEventManager />} />
            </Route>
          </Route>
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;
